import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderCreditCardsIfNeeded } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import { getShouldShowCompleteAccountBanner } from '@/redux/modules/account/user/profileCompleted/profileCompleted.selectors';
import { fetchBidderDataIfNeeded } from '@/redux/modules/account/user/user.actions';
import { openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackCompleteAccountSetupBannerClicked } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

interface CompleteAccountBannerProps {
    className?: string;
}

export const CompleteAccountBanner = ({ className }: CompleteAccountBannerProps) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const showCompleteAccount = useAppSelector(getShouldShowCompleteAccountBanner);
    const trackCompleteAccountSetupBannerClicked = useTrackCompleteAccountSetupBannerClicked();

    const handleSetupLink = useCallback(() => {
        dispatch(openCompleteAccountSignUpModal());
        trackCompleteAccountSetupBannerClicked();
    }, [dispatch, trackCompleteAccountSetupBannerClicked]);

    useEffect(() => {
        dispatch(fetchBidderDataIfNeeded({ force: true }));
        dispatch(fetchBidderCreditCardsIfNeeded());
    }, [dispatch]);

    return (
        showCompleteAccount && (
            <div
                className={cn(
                    'mx-auto mt-lg flex w-full max-w-[1240px] gap-sm border border-border-info bg-background-info-light p-sm smMax:mx-0 smMax:w-auto',
                    isWhiteLabel ? 'mb-lg' : 'mb-0',
                    className
                )}
                data-testid="complete-account-banner"
            >
                <Info
                    className="shrink-0"
                    color="info"
                    size="sm"
                    style="fill"
                />
                <BodySecondary>
                    <InlineButton
                        onClick={handleSetupLink}
                        size="sm"
                    >
                        {formatMessage(['completeAccount', 'setupLink'])}
                    </InlineButton>
                    <FormattedMessage id="completeAccount.toSaveTimeBidding" />
                </BodySecondary>
            </div>
        )
    );
};

export default CompleteAccountBanner;
